import React from "react";
import { AlertTriangle } from "lucide-react";
import { declaredAnswerProvenance, declaredAnswerText } from "../utils/declaredConsequence";
import { signalSourceLabel } from "../utils/signalSourceLabel";

/**
 * Behavior 360 (§11f): what has been declared about this asset, what it can do, what it did,
 * and what changed — four columns over the composition endpoint, plus decisions and findings.
 *
 * Rendering rules the backend contract exists for:
 * - Every empty state is the server's sentence, never a blank — the withheld reasons and
 *   absence notes ARE the content.
 * - Tensions are open findings only; closed ones render under History with their disposition.
 * - Nothing here computes a verdict: this panel formats the DTO and links out.
 */

const COMPLETENESS_STYLE = {
  Current: "bg-emerald-50 text-emerald-700 border-emerald-200",
  LastKnownReadFailed: "bg-amber-50 text-amber-800 border-amber-200",
  Unavailable: "bg-red-50 text-red-700 border-red-200",
  NotApplicable: "bg-theme-hover text-content-secondary border-border",
};

const ENVELOPE_STYLE = {
  Ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Warming: "bg-blue-50 text-blue-700 border-blue-200",
  Unavailable: "bg-amber-50 text-amber-800 border-amber-200",
  NotApplicable: "bg-theme-hover text-content-secondary border-border",
};

const day = (v) => (v ? String(v).slice(0, 10) : null);

function Chip({ className, children }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded border text-[11px] ${className}`}>
      {children}
    </span>
  );
}

function Column({ title, question, children }) {
  return (
    <div className="border border-border rounded-lg p-3 bg-theme-primary min-w-0">
      <div className="text-xs font-semibold text-content-primary">{title}</div>
      <div className="text-[11px] text-content-secondary mb-2">{question}</div>
      <div className="space-y-2 text-xs text-content-primary">{children}</div>
    </div>
  );
}

function Fact({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div>
      <span className="text-content-secondary">{label}: </span>
      <span>{value}</span>
    </div>
  );
}

/** Roadmap item D: one declared answer's own provenance — where its current text was first
 *  written — under its line. Nothing when the question was not answered. */
function DeclaredAnswerProvenance({ answer }) {
  const text = declaredAnswerProvenance(answer);
  if (!text) return null;
  return (
    <div className="text-[11px] text-content-secondary" data-testid="declared-answer-provenance">
      {text}
    </div>
  );
}

const NOT_ANSWERED_NOTE = "not answered — see the Playbook tab";

function AbsenceNote({ children }) {
  return (
    <div role="note" className="flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5">
      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

// Boundary dimensions, labelled in the order the server sends them (R1's order).
const DIMENSION_LABEL = {
  identity: "Identity",
  tool: "Tool / MCP",
  resource: "App / resource",
  data: "Data",
  destination: "Destination",
  action: "Action",
  artifacts: "Artifacts",
};

function EntryRow({ e }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span>{e.name}</span>
      <Chip className="bg-violet-50 text-violet-700 border-violet-200">{e.edgeType}</Chip>
      {e.capabilityClass && e.capabilityClass !== "unknown" && (
        <Chip className="bg-theme-hover text-content-secondary border-border">
          {e.capabilityClass}
        </Chip>
      )}
      {e.dataSensitivity && (
        <Chip className="bg-red-50 text-red-700 border-red-200">{e.dataSensitivity}</Chip>
      )}
    </div>
  );
}

function ResourceRow({ r }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span title={r.resourceId}>{r.resourceName ?? r.resourceId}</span>
        {r.confidence != null && (
          <Chip className="bg-theme-hover text-content-secondary border-border">{r.confidence}</Chip>
        )}
      </div>
      {r.grantNames?.length > 0 && (
        <div className="text-[11px] text-content-secondary">via {r.grantNames.join(", ")}</div>
      )}
    </div>
  );
}

/**
 * One boundary dimension. Every dimension renders, whatever it carries: an uncovered one shows
 * the server's reason as an absence note, never an empty list. Last-known entries an Unavailable
 * dimension carries still render below the note. A reason on a Ready dimension is a limit, not a
 * failure, so it renders muted.
 */
function BoundaryDimension({ d }) {
  const entries = d.entries ?? [];
  const resources = d.resources ?? [];
  return (
    <div className="space-y-0.5" data-testid={`boundary-dimension-${d.dimension}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-medium">{DIMENSION_LABEL[d.dimension] || d.dimension}</span>
        <Chip className={ENVELOPE_STYLE[d.status] || ENVELOPE_STYLE.NotApplicable}>{d.status}</Chip>
        {d.source && <span className="text-[11px] text-content-secondary">from {signalSourceLabel(d.source)}</span>}
      </div>
      {d.status === "Unavailable" ? (
        <AbsenceNote>{d.withheldReason || "Not covered."}</AbsenceNote>
      ) : (
        d.withheldReason && (
          <div className="text-[11px] text-content-secondary">{d.withheldReason}</div>
        )
      )}
      {(entries.length > 0 || resources.length > 0) && (
        <div className="space-y-1">
          {entries.map((e) => (
            <EntryRow key={`${e.nodeId}-${e.edgeType}`} e={e} />
          ))}
          {resources.map((r) => (
            <ResourceRow key={r.resourceId} r={r} />
          ))}
        </div>
      )}
    </div>
  );
}

const TIER_LABEL = { 1: "Tier 1 — most critical", 2: "Tier 2 — standard", 3: "Tier 3 — sandbox" };

/**
 * `showBoundary` is the AI Intelligence preview flag, passed by the dashboard: the Permitted
 * column (the boundary contract) is a preview surface and is hidden while it is off. The
 * "API keys from …" line is not boundary data, so it moves into Declared instead of going with it.
 */
export default function AiBehavior360Panel({ data, onShowTab, showBoundary = false }) {
  if (!data) return null;
  const { declared, permitted, observed, expected, decisions, findings } = data;

  const linkOut = (tab, label) => (
    <button
      onClick={() => onShowTab?.(tab)}
      className="text-[11px] text-blue-600 hover:underline"
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-3">
      <div className={`grid grid-cols-1 md:grid-cols-2 ${showBoundary ? "xl:grid-cols-4" : "xl:grid-cols-3"} gap-3`}>
        {/* Declared & governance context — accountability and placement, not intent. */}
        <Column
          title="Declared & governance"
          question="What has the organization or provider declared about this asset?"
        >
          {declared.accountabilityAbsent
            ? <AbsenceNote>{declared.accountabilityAbsentNote}</AbsenceNote>
            : (
              <>
                <Fact label="Owner" value={declared.ownerUpn} />
                <Fact label="Sponsor" value={declared.sponsorName} />
              </>
            )}
          <Fact label="Tier" value={declared.tier != null ? TIER_LABEL[declared.tier] : "not set"} />
          <Fact label="Governance" value={declared.governanceState} />
          <Fact label="Business anchor" value={declared.businessAnchorLabel} />
          <Fact label="Criticality" value={declared.businessCriticality} />
          {declared.registeredIn?.length > 0 ? (
            <div>
              <span className="text-content-secondary">Registered in: </span>
              {declared.registeredIn.map((s) => (
                <Chip key={s} className="bg-blue-50 text-blue-700 border-blue-200 mr-1">{s}</Chip>
              ))}
            </div>
          ) : (
            <div className="text-content-secondary">No platform registration recorded.</div>
          )}
          {declared.hasPlaybook ? (
            <div className="pt-1 border-t border-border">
              <span className="text-content-secondary">Declared purpose (playbook v{declared.playbookVersion}): </span>
              <span>{declared.declaredPurpose || NOT_ANSWERED_NOTE}</span>
              <DeclaredAnswerProvenance answer={declared.declaredConsequence?.serves} />
            </div>
          ) : (
            <div className="text-[11px] text-content-secondary pt-1">
              Nothing records what this asset is <i>supposed</i> to do — fill its Playbook to
              declare that. Owner, tier and anchor are edited above this panel.
            </div>
          )}
          {/* Roadmap item D: the rest of the consequence the owner declared, verbatim, each answer
              with its own provenance (where that text was first written — an unrelated later
              save does not re-date it). A skipped question inside an existing playbook says so,
              like the purpose line above; no playbook at all adds nothing — the note above
              already says it. */}
          {declared.hasPlaybook && (
            <>
              <Fact
                label="What breaks if stopped"
                value={declaredAnswerText(declared.declaredConsequence?.breaksIfStopped) || NOT_ANSWERED_NOTE}
              />
              <DeclaredAnswerProvenance answer={declared.declaredConsequence?.breaksIfStopped} />
              <Fact
                label="Tell first"
                value={declaredAnswerText(declared.declaredConsequence?.notifyFirst) || NOT_ANSWERED_NOTE}
              />
              <DeclaredAnswerProvenance answer={declared.declaredConsequence?.notifyFirst} />
            </>
          )}
          {/* §11l: the owner's expected-behavior declaration confronted with observation.
              Sentences, windows and sources come from the server — this renders, never judges. */}
          {declared.declaredBehavior && (
            <div className="pt-1 border-t border-border space-y-1">
              {declared.declaredBehavior.models && (
                <div>
                  <span className="text-content-secondary">Expected models: </span>
                  {declared.declaredBehavior.models.join(", ")}
                </div>
              )}
              {declared.declaredBehavior.resources && (
                <div>
                  <span className="text-content-secondary">Expected resources: </span>
                  {declared.declaredBehavior.resources.join(", ")}
                </div>
              )}
              {declared.declaredBehavior.originCountries && (
                <div>
                  <span className="text-content-secondary">Expected authentication origins: </span>
                  {declared.declaredBehavior.originCountries.join(", ")}
                </div>
              )}
              {/* One line per DISTINCT sentence: three dimensions withheld for the same
                  reason ("declared today") must not say it three times. Comparisons that
                  actually differ — a mismatch names its dimension's values — stay separate. */}
              {Object.values((declared.declaredComparisons || []).reduce((acc, c) => {
                if (acc[c.sentence]) acc[c.sentence].dimensions.push(c.dimension);
                else acc[c.sentence] = { ...c, dimensions: [c.dimension] };
                return acc;
              }, {})).map((c) => (
                <div
                  key={c.dimensions.join("+")}
                  className={`text-[11px] rounded px-1.5 py-1 ${
                    c.compared && c.unmatched.length > 0
                      ? "bg-amber-50 text-amber-900 border border-amber-200"
                      : "text-content-secondary"
                  }`}
                  title={`${c.dimensions.join(", ")} · declared in playbook v${c.declaredInVersion}, effective ${String(c.declaredSince).slice(0, 10)} · ${signalSourceLabel(c.source)}`}
                >
                  {c.sentence}
                </div>
              ))}
            </div>
          )}
          {!showBoundary && permitted.credentials.length > 0 && (
            <div className="pt-1 border-t border-border">
              {permitted.credentials.map((c) => (
                <Fact key={c.source} label={`API keys from ${signalSourceLabel(c.source)}`} value={c.activeKeyCount} />
              ))}
            </div>
          )}
        </Column>

        {/* Permitted — the boundary contract: per-source completeness, then seven dimensions.
            A preview surface: hidden while the AI Intelligence preview flag is off. */}
        {showBoundary && (
        <Column title="Permitted" question="What can it do?">
          {permitted.sources.map((s) => (
            <div key={s.source} className="flex items-start gap-1.5 flex-wrap">
              <span className="font-medium">{signalSourceLabel(s.source)}</span>
              <Chip className={COMPLETENESS_STYLE[s.completeness] || COMPLETENESS_STYLE.NotApplicable}>
                {s.completeness}
              </Chip>
              {s.notLiveVerified && (
                <Chip className="bg-violet-50 text-violet-700 border-violet-200">not live-verified</Chip>
              )}
              {s.note && <div className="w-full text-[11px] text-content-secondary">{s.note}</div>}
            </div>
          ))}
          {permitted.note && <div className="text-content-secondary">{permitted.note}</div>}
          {/* Seven boundary dimensions, in server order. No length gate: an uncovered
              dimension renders with its reason rather than disappearing. */}
          <div className="space-y-2 pt-1 border-t border-border">
            {(permitted.boundary?.dimensions ?? []).map((d) => (
              <BoundaryDimension key={d.dimension} d={d} />
            ))}
          </div>
          {permitted.credentials.length > 0 && (
            <div className="pt-1 border-t border-border">
              {permitted.credentials.map((c) => (
                <Fact key={c.source} label={`API keys from ${signalSourceLabel(c.source)}`} value={c.activeKeyCount} />
              ))}
            </div>
          )}
          {linkOut("graph", "Open in relationship graph →")}
        </Column>
        )}

        {/* Observed — window-bounded, with unavailable never rendered as zero. */}
        <Column title="Observed" question="What did it do?">
          {observed.profiles.length === 0 && (
            <div className="text-content-secondary">
              No behavioral telemetry covers this asset — Axaris cannot see its activity, which is
              not the same as the asset being idle.
            </div>
          )}
          {observed.profiles.map((p) => (
            <div key={p.source} className="space-y-0.5">
              <div className="flex items-center gap-1.5">
                <span className="font-medium">{signalSourceLabel(p.source)}</span>
                <Chip className={p.isCurrent
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-amber-50 text-amber-800 border-amber-200"}>
                  {p.isCurrent ? "current" : "not current"}
                </Chip>
              </div>
              <div className="text-[11px] text-content-secondary">
                {day(p.observationStart)} → {day(p.observationEnd)} · {p.signInCount} sign-in(s)
                {p.failedSignInCount > 0 ? `, ${p.failedSignInCount} refused` : ""} ·{" "}
                {p.distinctResourceCount} resource(s)
              </div>
            </div>
          ))}
          <div className="pt-1 border-t border-border space-y-1">
            {observed.usage.status === "Present" && observed.usage.groups.map((g) => (
              <div key={`${g.provider}-${g.currency}`}>
                <span className="font-medium">{g.provider}</span>{" "}
                <span>
                  {g.currency} {Number(g.costLast30Days).toFixed(2)} ·{" "}
                  {Number(g.tokensLast30Days).toLocaleString()} tokens (30d)
                </span>
                {g.dominantModel && (
                  <span className="text-content-secondary"> · mostly {g.dominantModel}</span>
                )}
              </div>
            ))}
            {observed.usage.status === "None" && (
              <div className="text-content-secondary">
                No metered usage attributed in the last 30 days — the applicable ledger was read
                and found none.
              </div>
            )}
            {observed.usage.status === "Unavailable" && (
              <AbsenceNote>
                The usage ledger has not read this asset's provider — usage is unknown, not zero.
              </AbsenceNote>
            )}
            {observed.usage.status === "NotApplicable" && (
              <div className="text-content-secondary">No metered-usage provider applies.</div>
            )}
          </div>
          {linkOut("activity", "Open activity trail →")}
        </Column>

        {/* Expected — the drift evaluator's own envelope, verbatim. */}
        <Column
          title="Expected"
          question={`What is normal (${expected.baselineDays}d baseline vs ${expected.recentDays}d recent), and did it change?`}
        >
          {expected.envelope.map((d) => (
            <div key={d.dimension} className="space-y-0.5">
              <div className="flex items-center gap-1.5">
                <span className="font-medium">{d.dimension.replace(/([A-Z])/g, " $1").trim()}</span>
                <Chip className={d.isDrifting
                  ? "bg-red-50 text-red-700 border-red-200"
                  : ENVELOPE_STYLE[d.status] || ENVELOPE_STYLE.NotApplicable}>
                  {d.isDrifting ? "drifting" : d.status}
                </Chip>
              </div>
              {d.status === "Ready" ? (
                <div className="text-[11px] text-content-secondary">
                  baseline {d.baselineValue} · recent {d.recentValue}
                  {d.isDrifting && d.driftSentence && (
                    <div className="text-red-700 mt-0.5">{d.driftSentence}</div>
                  )}
                </div>
              ) : (
                <div className="text-[11px] text-content-secondary">{d.withheldReason}</div>
              )}
            </div>
          ))}
        </Column>
      </div>

      {/* Decisions & exceptions — not a leg: judgments people recorded about this asset. */}
      {(decisions.suppressions.length > 0 || decisions.behaviourAllowlisted
        || decisions.resolutionHistory.length > 0) && (
        <div className="border border-border rounded-lg p-3">
          <div className="text-xs font-semibold text-content-primary mb-1.5">
            Decisions &amp; exceptions
          </div>
          <div className="space-y-1 text-xs">
            {decisions.suppressions.map((s, i) => (
              <div key={i}>
                <Chip className="bg-theme-hover text-content-secondary border-border mr-1.5">
                  {s.status}
                </Chip>
                <span className="font-medium">{s.ruleCode}</span>
                <span className="text-content-secondary">
                  {" "}— "{s.reason}"{s.suppressedByUserName ? ` (${s.suppressedByUserName}` : ""}
                  {s.suppressedByUserName ? `, ${day(s.suppressedAt)})` : ""}
                </span>
              </div>
            ))}
            {decisions.behaviourAllowlisted && (
              <div className="text-content-secondary">
                On the behavioral allowlist — a settings statement that this asset's activity
                pattern is expected. Unlike a suppression it carries no per-entry reason or
                decision owner.
              </div>
            )}
            {decisions.resolutionHistory.map((h) => (
              <div key={h.ruleCode} className="text-content-secondary">
                {h.ruleCode}: resolved {h.timesResolved}×, last {day(h.lastResolvedAt)}
                {h.lastDisposition ? ` (${h.lastDisposition})` : ""}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open tensions vs history — never mixed. */}
      <div className="border border-border rounded-lg p-3">
        <div className="text-xs font-semibold text-content-primary mb-1.5">
          Current tensions{findings.open.length > 0 ? ` (${findings.open.length})` : ""}
        </div>
        {findings.open.length === 0 ? (
          <div className="text-xs text-content-secondary">No open findings on this asset.</div>
        ) : (
          <div className="space-y-1 text-xs">
            {findings.open.map((f) => (
              <div key={f.unifiedRiskId} className="flex items-center gap-1.5 flex-wrap">
                <Chip className="bg-red-50 text-red-700 border-red-200">{f.severity}</Chip>
                <span>{f.title}</span>
                {linkOut("risks", "view →")}
              </div>
            ))}
          </div>
        )}
        {findings.history.length > 0 && (
          <div className="mt-2 pt-2 border-t border-border">
            <div className="text-[11px] font-semibold text-content-secondary uppercase mb-1">
              History — closed findings, with why they closed
            </div>
            <div className="space-y-0.5 text-[11px] text-content-secondary">
              {findings.history.map((f) => (
                <div key={f.unifiedRiskId}>
                  {f.title} · closed {day(f.closedAt)}
                  {f.closureDisposition ? ` (${f.closureDisposition})` : ""}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
